import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMapMarkerAlt, faArrowLeft, faPlus, faPenToSquare, faTrash,
  faXmark, faCheckCircle, faTimesCircle, faTag,
} from '@fortawesome/free-solid-svg-icons';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import useAutoDismissSnack from '../../../Hooks/AutoDismissSnack';

const BASE = `${BASE_URL}`;

function TableHeaders() {
  return (
    <thead className="bg-gradient-to-r from-orange-400 to-orange-600">
      <tr>
        {['Identificador', 'Descripción', 'Estado', 'Vehículo (Placa)', 'Propietario', 'Apartamento', 'Acciones'].map(h => (
          <th key={h} className="px-5 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">{h}</th>
        ))}
      </tr>
    </thead>
  );
}

function ExternoRow({ p, onEdit, onDelete }) {
  return (
    <tr className="hover:bg-gray-50 transition-colors">
      <td className="px-5 py-4 text-center font-bold text-gray-900">{p.identificador}</td>
      <td className="px-5 py-4 text-center text-sm text-gray-600">{p.descripcion || <span className="text-gray-300">—</span>}</td>
      <td className="px-5 py-4 text-center">
        <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${p.estado === 'Disponible' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
          {p.estado}
        </span>
      </td>
      <td className="px-5 py-4 text-center text-sm">
        {p.placa
          ? <div><div className="font-bold">{p.placa}</div><div className="text-xs text-gray-400">{p.marca} {p.modelo}</div></div>
          : <span className="text-gray-300">—</span>}
      </td>
      <td className="px-5 py-4 text-center text-sm text-gray-800">
        {p.usuario_nombre ? `${p.usuario_nombre} ${p.usuario_apellido}` : <span className="text-gray-300">—</span>}
      </td>
      <td className="px-5 py-4 text-center text-sm text-gray-800">{p.num_apartamento || <span className="text-gray-300">—</span>}</td>
      <td className="px-5 py-4 text-center">
        <div className="flex justify-center gap-2">
          <button
            onClick={() => onEdit(p)}
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm transition-colors"
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            Editar
          </button>
          {p.estado === 'Disponible' && (
            <button
              onClick={() => onDelete(p.id)}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm transition-colors"
            >
              <FontAwesomeIcon icon={faTrash} />
              Eliminar
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

function ControlExternalParkings() {
  const navigate = useNavigate();

  const [externos, setExternos] = useState([]);
  const [variaciones, setVariaciones] = useState([]);
  const [extStats, setExtStats] = useState({ total: 0, ocupados: 0, disponibles: 0 });
  const [search, setSearch] = useState('');

  // variaciones panel
  const [varOpen, setVarOpen] = useState(false);
  const [varName, setVarName] = useState('');
  const [varEditOpen, setVarEditOpen] = useState(false);
  const [varEditItem, setVarEditItem] = useState(null);

  // confirm dialog
  const [confirmDialog, setConfirmDialog] = useState({ open: false, message: '', onConfirm: null });
  const openConfirm = (message, onConfirm) => setConfirmDialog({ open: true, message, onConfirm });
  const closeConfirm = () => setConfirmDialog({ open: false, message: '', onConfirm: null });

  // add/edit modal
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [addVariacionId, setAddVariacionId] = useState('');
  const [form, setForm] = useState({ identificador: '', descripcion: '' });

  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState('');
  const [snackMessage, setSnackMessage] = useState('');
  useAutoDismissSnack(openSnack, setOpenSnack);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchExternos();
    fetchVariaciones();
    fetchStats();
  }, []);

  const snack = (status, msg) => {
    setSnackStatus(status);
    setSnackMessage(msg);
    setOpenSnack(true);
  };

  const fetchExternos = async () => {
    try {
      const res = await axios.get(`${BASE}/parkings/externos`);
      setExternos(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchVariaciones = async () => {
    try {
      const res = await axios.get(`${BASE}/variaciones-ext`);
      setVariaciones(res.data);
    } catch (e) { console.error(e); }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BASE}/parkings/externos/stats`);
      setExtStats(res.data);
    } catch (e) { console.error(e); }
  };

  // Variaciones CRUD
  const handleAddVariacion = async (e) => {
    e.preventDefault();
    if (!varName.trim()) return;
    try {
      await axios.post(`${BASE}/variaciones-ext`, { nombre: varName.trim() });
      snack('success', 'Variación creada correctamente');
      setVarName('');
      setVarOpen(false);
      fetchVariaciones();
    } catch (err) {
      snack('error', err.response?.data?.error || 'Error al crear variación');
    }
  };

  const handleEditVariacionSubmit = async (e) => {
    e.preventDefault();
    if (!varEditItem?.nombre?.trim()) return;
    try {
      await axios.put(`${BASE}/variaciones-ext/${varEditItem.id}`, { nombre: varEditItem.nombre.trim() });
      snack('success', 'Variación actualizada correctamente');
      setVarEditOpen(false);
      setVarEditItem(null);
      fetchVariaciones();
    } catch (err) {
      snack('error', err.response?.data?.error || 'Error al actualizar variación');
    }
  };

  const handleDeleteVariacion = (v) => {
    openConfirm(`¿Eliminar la variación "${v.nombre}"? Esta acción no se puede deshacer.`, async () => {
      closeConfirm();
      try {
        await axios.delete(`${BASE}/variaciones-ext/${v.id}`);
        snack('success', 'Variación eliminada correctamente');
        fetchVariaciones();
      } catch (err) {
        snack('error', err.response?.data?.error || 'Error al eliminar variación');
      }
    });
  };

  // External parkings CRUD
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BASE}/parkings/externos`, {
        identificador: form.identificador,
        descripcion: form.descripcion,
        id_variacion: addVariacionId || null,
      });
      snack('success', 'Parqueadero externo agregado correctamente');
      setAddOpen(false);
      setForm({ identificador: '', descripcion: '' });
      setAddVariacionId('');
      fetchExternos();
      fetchStats();
    } catch (err) {
      snack('error', err.response?.data?.error || 'Error al agregar parqueadero externo');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BASE}/parkings/externos/${editItem.id}`, {
        identificador: editItem.identificador,
        descripcion: editItem.descripcion,
        id_variacion: editItem.id_variacion || null,
      });
      snack('success', 'Parqueadero externo actualizado correctamente');
      setEditOpen(false);
      fetchExternos();
    } catch (err) {
      snack('error', err.response?.data?.error || 'Error al actualizar parqueadero externo');
    }
  };

  const handleDelete = (id) => {
    openConfirm('¿Está seguro de eliminar este parqueadero externo?', async () => {
      closeConfirm();
      try {
        await axios.delete(`${BASE}/parkings/externos/${id}`);
        snack('success', 'Parqueadero externo eliminado correctamente');
        fetchExternos();
        fetchStats();
      } catch (err) {
        snack('error', err.response?.data?.error || 'Error al eliminar parqueadero externo');
      }
    });
  };

  // Groups
  const varConParkings = new Set(externos.map(p => p.id_variacion).filter(Boolean));

  const filtered = externos.filter(p =>
    p.identificador.toLowerCase().includes(search.toLowerCase()) ||
    (p.descripcion && p.descripcion.toLowerCase().includes(search.toLowerCase())) ||
    (p.placa && p.placa.toLowerCase().includes(search.toLowerCase())) ||
    (p.usuario_nombre && p.usuario_nombre.toLowerCase().includes(search.toLowerCase())) ||
    (p.variacion_nombre && p.variacion_nombre.toLowerCase().includes(search.toLowerCase()))
  );

  const sinVariacion = filtered.filter(p => !p.id_variacion);
  const groups = [
    ...variaciones.map(v => ({
      key: v.id,
      label: v.nombre,
      isVariacion: true,
      items: filtered.filter(p => p.id_variacion === v.id),
    })),
    { key: 'sin', label: 'Sin variación', isVariacion: false, items: sinVariacion },
  ].filter(g => g.items.length > 0 || (!search && g.isVariacion));

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 sm:p-6 lg:p-8">
        <div className="max-w-5xl mx-auto">

          {/* Snackbar */}
          {openSnack && (
            <div className={`fixed top-4 right-4 z-[9999] p-4 rounded-lg shadow-lg ${snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'} text-white`}>
              <div className="flex items-center gap-2">
                <span>{snackMessage}</span>
                <button onClick={() => setOpenSnack(false)}>
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            </div>
          )}

          {/* Back */}
          <button
            onClick={() => navigate('/adminParkings')}
            className="mb-6 flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-sm"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
            Volver
          </button>

          <h1 className="text-4xl font-bold text-gray-800 text-center mb-10">
            Parqueaderos Externos
          </h1>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Total', value: extStats.total, color: 'text-gray-800', icon: faMapMarkerAlt, iconColor: 'text-orange-400' },
              { label: 'Ocupados', value: extStats.ocupados, color: 'text-red-600', icon: faTimesCircle, iconColor: 'text-red-400' },
              { label: 'Disponibles', value: extStats.disponibles, color: 'text-green-600', icon: faCheckCircle, iconColor: 'text-green-400' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-2xl shadow-md p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500 mb-1">{s.label}</p>
                    <p className={`text-3xl font-bold ${s.color}`}>{s.value}</p>
                  </div>
                  <FontAwesomeIcon icon={s.icon} className={`h-10 w-10 ${s.iconColor}`} />
                </div>
              </div>
            ))}
          </div>

          {/* Variaciones panel */}
          <div className="bg-white rounded-2xl shadow-md p-5 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-semibold text-gray-700 flex items-center gap-2">
                <FontAwesomeIcon icon={faTag} className="text-orange-400" />
                Variaciones
              </h2>
              <button
                onClick={() => { setVarName(''); setVarOpen(true); }}
                className="flex items-center gap-1 px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg font-medium transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} />
                Agregar
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {variaciones.length === 0 && (
                <span className="text-sm text-gray-400">No hay variaciones creadas. Agrega una para organizar los parqueaderos.</span>
              )}
              {variaciones.map(v => (
                <div key={v.id} className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-full px-3 py-1">
                  <span className="text-sm text-orange-800 font-medium">{v.nombre}</span>
                  <button
                    onClick={() => { setVarEditItem({ ...v }); setVarEditOpen(true); }}
                    className="ml-1 text-blue-400 hover:text-blue-600 transition-colors"
                    title="Editar variación"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} className="h-3 w-3" />
                  </button>
                  {!varConParkings.has(v.id) && (
                    <button
                      onClick={() => handleDeleteVariacion(v)}
                      className="text-red-400 hover:text-red-600 transition-colors"
                      title="Eliminar variación"
                    >
                      <FontAwesomeIcon icon={faTrash} className="h-3 w-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Search + Add */}
          <div className="flex flex-col sm:flex-row gap-3 justify-between mb-4">
            <input
              type="text"
              placeholder="Buscar por identificador, variación, descripción, placa, propietario..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field flex-1 sm:max-w-md"
            />
            <button
              onClick={() => { setForm({ identificador: '', descripcion: '' }); setAddVariacionId(''); setAddOpen(true); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold transition-colors shadow-sm whitespace-nowrap"
            >
              <FontAwesomeIcon icon={faPlus} />
              Agregar Parqueadero Externo
            </button>
          </div>

          {/* Grouped tables */}
          {groups.length === 0 ? (
            <div className="bg-white rounded-2xl shadow-md p-10 text-center text-gray-400">No hay parqueaderos externos registrados</div>
          ) : groups.map(g => (
            <div key={g.key} className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
              <div className={`px-5 py-3 font-semibold text-base flex items-center gap-2 ${g.isVariacion ? 'bg-orange-50 text-orange-700 border-b border-orange-100' : 'bg-gray-50 text-gray-600 border-b border-gray-100'}`}>
                <FontAwesomeIcon icon={faTag} className={g.isVariacion ? 'text-orange-400' : 'text-gray-400'} />
                {g.label}
                <span className="ml-auto text-sm font-normal opacity-70">{g.items.length} parqueadero{g.items.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <TableHeaders />
                  <tbody className="bg-white divide-y divide-gray-200">
                    {g.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="text-center py-6 text-gray-400 text-sm">No hay parqueaderos en esta variación</td>
                      </tr>
                    ) : g.items.map(p => (
                      <ExternoRow
                        key={p.id}
                        p={p}
                        onEdit={item => { setEditItem({ ...item }); setEditOpen(true); }}
                        onDelete={handleDelete}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Modal Agregar Variación */}
      {varOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Nueva Variación</h2>
              <button onClick={() => setVarOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddVariacion}>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Ej: Sector Norte, Nivel 1..."
                  value={varName}
                  onChange={e => setVarName(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setVarOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold">Crear</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Variación */}
      {varEditOpen && varEditItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Editar Variación</h2>
              <button onClick={() => setVarEditOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleEditVariacionSubmit}>
              <div className="p-6">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  className="input-field"
                  value={varEditItem.nombre}
                  onChange={e => setVarEditItem({ ...varEditItem, nombre: e.target.value })}
                  required
                  autoFocus
                />
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setVarEditOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold">Guardar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Agregar Parqueadero */}
      {addOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Agregar Parqueadero Externo</h2>
              <button onClick={() => setAddOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleAddSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Identificador *</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Ej: EXT-01, P-A1, Bodega-B3..."
                    value={form.identificador}
                    onChange={e => setForm({ ...form, identificador: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variación</label>
                  <select
                    className="input-field"
                    value={addVariacionId}
                    onChange={e => setAddVariacionId(e.target.value)}
                  >
                    <option value="">Sin variación</option>
                    {variaciones.map(v => (
                      <option key={v.id} value={v.id}>{v.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <input
                    type="text"
                    className="input-field"
                    placeholder="Ej: Sector norte, frente al portón principal..."
                    value={form.descripcion}
                    onChange={e => setForm({ ...form, descripcion: e.target.value })}
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setAddOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-semibold">Agregar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Editar Parqueadero */}
      {editOpen && editItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-800">Editar Parqueadero Externo</h2>
              <button onClick={() => setEditOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleEditSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Identificador *</label>
                  <input
                    type="text"
                    className="input-field"
                    value={editItem.identificador}
                    onChange={e => setEditItem({ ...editItem, identificador: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Variación</label>
                  <select
                    className="input-field"
                    value={editItem.id_variacion || ''}
                    onChange={e => setEditItem({ ...editItem, id_variacion: e.target.value || null })}
                  >
                    <option value="">Sin variación</option>
                    {variaciones.map(v => (
                      <option key={v.id} value={v.id}>{v.nombre}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
                  <input
                    type="text"
                    className="input-field"
                    value={editItem.descripcion || ''}
                    onChange={e => setEditItem({ ...editItem, descripcion: e.target.value })}
                  />
                </div>
              </div>
              <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                <button type="button" onClick={() => setEditOpen(false)} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
                <button type="submit" className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold">Guardar Cambios</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      {confirmDialog.open && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faTrash} className="text-red-500" />
                Confirmar eliminación
              </h2>
            </div>
            <div className="p-6 text-gray-700">{confirmDialog.message}</div>
            <div className="bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
              <button onClick={closeConfirm} className="px-5 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium">Cancelar</button>
              <button onClick={confirmDialog.onConfirm} className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold">Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ControlExternalParkings;
